<template>
  <div class="container-fluid">
    <div class="row fme-new-edit__title fme-montserrat-font">
      <div class="col-md-9">
        <h3>
          {{
            $t(`supervision.crud.page.${crudCurrentPageTitle}`) | textUpperCase
          }}
        </h3>
      </div>

      <div class="col-md-3 fme-new-edit__buttons text-right">
        <BaseButtonEvent
          :btText="$t('supervision.crud.button.goBack')"
          btClickType="goBack"
          btId="newEditeGoBack"
          btClickValue=""
          btUrlGoBack="CrudRegionListPage"
          btCss="btn fme-btn fme-btn-secondary"
        />

        <span v-if="isUserAllowToEdit">        
          <BaseButtonEvent
            v-if="regionDeactivatedCrud === null"
            :btText="$t('supervision.crud.button.disable')"
            btClickType="sendDeleteWarning"
            :btClickValue="id"
            btId="regionIdCrud"
            :btUrlGo="urlDeactivate"
            btCss="btn fme-btn fme-btn-primary"
            btUrlGoBack="CrudRegionListPage"
            :nameAlert="regionName"
            prefixAlert="a"
            typeAlert="região"
            dependentAlert="locais"
            :listAlert="regionLocations"
          />
          <BaseButtonEvent
            v-else
            :btText="$t('supervision.crud.button.enable')"
            btClickType="sendActivateCrud"
            :btClickValue="id"
            btId="regionIdCrud"
            :btUrlGo="urlActivate"
            btCss="btn fme-btn fme-btn-primary fme-btn-back"
            btUrlGoBack="CrudRegionListPage"
          />
        </span>
      </div>
    </div>

    <div class="row">
      <div class="col-md-8">
        <BaseInput
          :inputCurrentValue="regionName"
          :inputTitle="$t('supervision.crud.form.region.name.title')"
          :inputPlaceholder="
            $t('supervision.crud.form.region.name.placeholder')
          "
          inputType="text"
          inputCss=""
          inputId="input-region-name"
          @returned-input-value="regionInput('name', $event)"
          :inputErrorText="regionError.name ? $t(regionError.name) : $t(regionError.role)"
          :inputDisabled="!isUserAllowToEdit"
        />
      </div>
      <div class="col-md-4">
        <BaseSelect
          selectId="select-timeZone"
          :selectTitle="$t('supervision.crud.form.region.timeZone.title')"
          :selectValues="selectItems"
          selectCss=""
          :selectCurrentValue="regionTimeZone"
          @returnedSelectValue="regionSelect('timeZone', $event)"
          :selectErrorText="$t(regionError.timeZone)"
          :selectReticence="enableReticence"
          :selectDisabled="!isUserAllowToEdit"
        />
      </div>
    </div>

    <div class="row fme-new-edit__row">
      <div v-if="isUserAllowToEdit && isUserAllowToSelectUsersRegion" class="col-md-6">
        <div class="fme-content__align--bottom">
          <BaseInputAutocomplete
            inputCurrentValue=""
            :inputTitle="$t('supervision.crud.form.route.stakeholder.title')"
            :inputPlaceholder="
              $t('supervision.crud.form.route.stakeholder.placeholder')
            "
            :inputUrl="usersRegionAutocompleteUrl"
            inputId="input-route-users-region"
            @returned-input-autocomplete-value="
              routeInput('usersRegion', $event)
            "
            :inputErrorText="$t(routeError.supervisor)"
            :isAllowToEdit="isUserAllowToEdit"
            :inputDisabled="!isUserAllowToEdit"
          />

          <ul class="fme-unordered-list__ul fme-montserrat-font">
            <li v-for="(item) in this.regionUsers" :key="item.uuid">
              {{ item.name }}

              <a
                v-if="isUserAllowToEdit"
                @click.prevent="deactivateUserRegion(item.userRegionUUID)"
                :title="$t('supervision.crud.button.delete')"
                class="fme-unordered-list__li--trash"
              >
                <font-awesome-icon
                  far
                  icon="trash-alt"
                  class="fme-unordered-list__li--trash-icon"
                />
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isUserAllowToEdit"  class="row fme-new-edit__row--last text-right">
      <div class="col-md-12">
        <BaseButtonEvent
          :btText="$t('supervision.crud.button.save')"
          :btClickType="saveValues.type"
          :btClickValue="newRegion"
          :btId="saveValues.id"
          :btUrlGo="saveValues.urlGo"
          :btUrlGoBack="saveValues.urlBack"
          btCss="btn fme-btn fme-btn-primary"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { fetchFeatureToggles } from '../../../../services/featureToggler';
import { checkIfUserIsAllowToEdit } from '../../../../utils/checkIfUserIsAllowToEdit';
import { getV3Token } from '../../../../utils/getV3Token'; 
import { getUserRole } from '../../../../utils/getUserRole'; 

import axios from "axios";
import { checkIfUserIsAllowToSelectUsersRegion } from "../../../../utils/checkIfUserIsAllowToSelectUsersRegion";

const BaseInput = () => import("@/components/fragment/input/BaseInput");
const BaseSelect = () => import("@/components/fragment/select/BaseSelect");
const BaseButtonEvent = () =>
  import("@/components/fragment/button/BaseButtonEvent");
const BaseInputAutocomplete = () =>
  import("@/components/fragment/input/BaseInputAutocomplete");

export default {
  name: "EditPage",
  async created() {
    this.clearStore();
    this.hasRegion();
    this.changeCrudCurrentPageTitle("region");
    await this.getFeatures();
    this.checkUserPermissions();

    if (this.isUserAllowToSelectUsersRegion) {
      this.getUsersRegion()
    }
  },
  update() {
    this.hasRegion();
  },
  beforeDestroy() {
    this.clearStore();
    this.changeErrors([]);
  },
  data() {
    return {
      enableReticence: false,
      selectItems: {
        default: "supervision.crud.select.timeZone.title",
        options: [
          { name: "America/Sao_Paulo", value: "timeZone.america_saoPaulo" },
          { name: "America/Recife", value: "timeZone.america_recife" },
          { name: "America/Cuiaba", value: "timeZone.america_cuiaba" },
          { name: "America/Manaus", value: "timeZone.america_manaus" },
        ],
      },
      saveValues: {
        type: "sendPutCrud",
        id: "btUpdateRegion",
        urlGo: `${process.env.VUE_APP_CRUD_URL_REGIONS}/${this.uuid}`,
        urlBack: "CrudRegionListPage",
      },
      regionError: {
        name: "",
        timeZone: "",
        role: "",
      },
      routeError: {
        supervisor: "",
      },
      isUserAllowToEdit: false,
      usersRegionAutocompleteUrl: `${process.env.VUE_APP_USERS_API}?fields=id,name,uuid&status=active&filters=name.lk.`,
      regionUsers: [],
      isUserAllowToSelectUsersRegion: false 
    };
  },
  props: {
    id: {
      type: String,
    },
    uuid: {
      type: String,
    },
  },
  components: {
    BaseInput,
    BaseSelect,
    BaseButtonEvent,
    BaseInputAutocomplete
  },
  computed: {
    ...mapGetters(["axiosHeaders1", "headersCrud"]),
    ...mapGetters("ModuleErrors", ["errorsList"]),
    ...mapGetters("ModuleSupervision", [
      "regionCrud",
      "regionIdCrud",
      "regionNameCrud",
      "regionTimeZoneCrud",
      "regionDeactivatedCrud",
      "crudCurrentPageTitle",
      "regionLocationsCrud",
    ]),
    regionName() {
      return this.regionNameCrud;
    },
    regionLocations() {
      if(this.regionLocationsCrud){
        return this.regionLocationsCrud.map((l) => l.name)
      }
    },
    regionTimeZone() {
      return this.regionTimeZoneCrud;
    },
    newRegion() {
      let region = {
        name: this.regionNameCrud,
        timezone: this.regionTimeZoneCrud,
      };

      return region;
    },
    urlActivate() {
      return `${process.env.VUE_APP_CRUD_URL_REGIONS}/${this.uuid}/activate`;
    },
    urlDeactivate() {
      return `${process.env.VUE_APP_CRUD_URL_REGIONS}/${this.uuid}/deactivate`;
    },
    urlDeleteSupervisor() {
      return `${process.env.VUE_APP_ROUTES_API}/${this.id}/lineups`;
    },
  },
  methods: {
    ...mapActions("ModuleErrors", ["changeErrors"]),
    ...mapActions("ModuleSupervision", [
      "changeRegionIdCrud",
      "changeRegionNameCrud",
      "changeRegionTimeZoneCrud",
      "changeRegionDeactivatedCrud",
      "changeCrudCurrentPageTitle",
      "changeRegionLocationsCrud",
      "clearUsersRegionCrud",
    ]),
    hasRegion() {
      if (this.id !== "" && !isNaN(this.id)) {
        let url = `${process.env.VUE_APP_CRUD_URL_REGIONS}/${this.uuid}`;
        axios
          .get(url, {
            headers: this.headersCrud,
          })
          .then((response) => {
            return response.data;
          })
          .then((region) => {
            this.setRegion(region);
          })
          .catch((e) => {
            if (e.response.status && e.response.status === 401) {
              this.logoutUser();
            } else {
              console.log(e);
            }
          });
      } else {
        this.$router.push({ name: "CrudRegionListPage" });
      }
    },
    setRegion(region) {
      if (region.timeZone) {
        this.enableReticence = true;
      }
      this.changeRegionIdCrud(region.uuid);
      this.changeRegionNameCrud(region.name);
      this.changeRegionTimeZoneCrud(region.timezone);
      this.changeRegionDeactivatedCrud(region.deactivatedAt);
      this.changeRegionLocationsCrud(region.locations);
    },
    regionInput(refValue, eventValue) {
      if (refValue === "name") {
        this.changeRegionNameCrud(eventValue);
      }
    },
    regionSelect(refValue, event) {
      if (refValue === "timeZone") {
        this.enableReticence = true;
        this.changeRegionTimeZoneCrud(event);
      }
    },
    clearStore() {
      this.changeRegionIdCrud("");
      this.changeRegionNameCrud("");
      this.changeRegionLocationsCrud([]);
      this.changeRegionTimeZoneCrud("");
    },
    getUsersRegion() {
      let url = `${process.env.VUE_APP_V3_BASE_URL}/user-regions?regionUUID=${this.uuid}&relation=user&load=active`;

      axios
        .get(url, {
          headers: this.headersCrud,
        })
        .then(({ data }) => {
          this.clearUsersRegionCrud();

          if (this.isReallyEmpty(data)) {
            this.$router.push({ name: "CrudRouteListPage" });
          } else {
            const users = data.rows.map(row => ({
              ...row.user,
              userRegionUUID: row.uuid
            }))
            this.regionUsers = users
          }
        })
        .catch((e) => {
          if (e.response.status && e.response.status === 401) {
            this.logoutUser();
          } else {
            console.log(e);
          }
        });
    },
    deactivateUserRegion(uuid) {
      let url = `${process.env.VUE_APP_V3_BASE_URL}/user-regions/${uuid}/deactivate`;

      axios
        .put(
          url,
          {},
          {
            headers: {...this.headersCrud}
          }
        )
        .then(() => {
          this.getUsersRegion();
        })
        .catch((e) => {
          if (e.response.status && e.response.status === 401) {
            this.logoutUser();
          } else {
            console.log(e);
          }
        });
    },
    routeInput(refValue, eventValue) {
  	  if (refValue === "usersRegion") {
        let item = {
          userUUID: eventValue.uuid,
          regionUUID: this.uuid
        };

        const userFound = this.regionUsers.find(user => user.uuid == item.userUUID)

        if (!userFound) {          
          const url = `${process.env.VUE_APP_V3_BASE_URL}/user-regions`
          axios
            .post(url, item, {
              headers: this.headersCrud
            })
            .then(() => {
              this.getUsersRegion();
            })
            .catch((e) => {
              if (e.response.status && e.response.status === 401) {
                this.logoutUser();
              } else {
                console.log(e);
              }
            });
        }
     
        
      }
    },
    async getFeatures() {
      try {
        const response = await fetchFeatureToggles(getV3Token());
        this.features = response;
      } catch (error) {
        console.error(error);
      }
    },
    checkUserPermissions() {
      if (this.features) {
        this.isUserAllowToEdit = checkIfUserIsAllowToEdit(this.features, getUserRole());
        this.isUserAllowToSelectUsersRegion = checkIfUserIsAllowToSelectUsersRegion(this.features)
      } else {
        console.error("Features are not available yet.");
      }
    }
  },
  filters: {},
  watch: {
    errorsList() {
      this.regionError.name = "";
      this.regionError.timeZone = "";
      this.regionError.role = "";

      this.errorsList.map((error) => {
        if (error.error === "User does not have permission to perform this action") {
          this.regionError.role = `errors.role.unauthorized`;
        }

        if (error.property.indexOf("name") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.regionError.name = `errors.name.required`;
          } else if (
            this.hasProperty(error.constraints, "IsRegionUniqueConstraint")
          ) {
            this.regionError.name = `errors.name.uniqueness`;
          }
        }

        if (error.property.indexOf("timeZone") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.regionError.timeZone = `errors.timeZone.required`;
          }
        }
      });
    },
  },
};
</script>

<style lang="scss">
.fme-unordered-list__ul {
  list-style-type: none;
  padding-left: 14px;
  li {
    padding-top: 14px;
    .fme-unordered-list__ul--itens {
      .fme-unordered-list__ul--iten {
        padding-top: 22px;
      }
    }
  }
}
.fme-unordered-list__li--trash {
  margin-left: 9px;
  cursor: pointer;
  .fme-unordered-list__li--trash-icon {
    color: $fme-color__yale-blue;
  }
}
</style>
